import type { PaymentIntentResult, SetupIntentResult, StripeElements, StripeExpressCheckoutElement, StripePaymentElement } from '@stripe/stripe-js';

export enum StripeErrorCodes {
    STRIPE_NOT_LOADED = 'STRIPE_NOT_LOADED',
    ELEMENTS_NOT_MOUNTED = 'ELEMENTS_NOT_MOUNTED',
}

export interface MountElementsProps {
    clientSecret: string;
    customerSessionClientSecret?: string;
}

export interface MountExpressCheckoutProps {
    id: string;
    onConfirm?: () => void;
    onError?: () => void;
}

export interface MountStripeElementsProps {
    id: string;
    onFocus?: () => void;
    onError?: () => void;
}

export type UseStripeReturn = {
    isPaymentElementReady: Ref<boolean>;
    isExpressCheckoutReady: Ref<boolean>;
    isPaymentElementLoaderStart: Ref<boolean>;
    isConfirmingPayment: Ref<boolean>;
    isConfirmingSetup: Ref<boolean>;
    elements: Ref<StripeElements | null>;
    paymentElement: Ref<StripePaymentElement | undefined>;
    mountElements: (props: MountElementsProps) => void;
    mountPayment: (props: MountStripeElementsProps) => void;
    mountExpressCheckout: (props: MountExpressCheckoutProps) => void;
    destroy: () => void;
    confirmPayment: () => Promise<PaymentIntentResult>;
    confirmSetup: () => Promise<SetupIntentResult>;
};

export const useStripe = (): UseStripeReturn => {
    // COMPOSABLES
    const { locale } = useTypedI18n();
    const { stripe } = useClientStripe();
    const { data } = useAuthUtils();
    const { frontendBaseUrl } = useRuntimeConfig().public;
    const route = useRoute();

    // DATA
    const isExpressCheckoutReady = ref<boolean>(false);
    const isPaymentElementReady = ref<boolean>(false);
    const isPaymentElementLoaderStart = ref<boolean>(false);
    const elements = ref<StripeElements | null>(null);
    const paymentElement = ref<StripePaymentElement | undefined>();
    const expressCheckoutElement = ref<StripeExpressCheckoutElement | undefined>();
    const isConfirmingPayment = ref<boolean>(false);
    const isConfirmingSetup = ref<boolean>(false);

    const mountElements = (props: MountElementsProps): void => {
        if (!stripe.value) {
            throw { code: StripeErrorCodes.STRIPE_NOT_LOADED };
        }

        elements.value = stripe.value.elements({
            appearance: { theme: 'stripe', variables: { gridRowSpacing: '1rem' } },
            locale: locale.value,
            ...props,
        });
    };

    const mountExpressCheckout = ({ id, ...handlers }: MountExpressCheckoutProps): void => {
        // Normal capture flow, only triggered if payment intent creation succeeds
        if (!elements.value) {
            throw { code: StripeErrorCodes.ELEMENTS_NOT_MOUNTED };
        }

        expressCheckoutElement.value = elements.value.create('expressCheckout', {
            buttonType: {
                paypal: 'paypal',
            },
            buttonHeight: 55,
            buttonTheme: {
                paypal: 'gold',
            },
            paymentMethods: {
                applePay: 'never',
                googlePay: 'never',
            },
        });
        expressCheckoutElement.value.mount(id);
        expressCheckoutElement.value?.once('ready', () => (isExpressCheckoutReady.value = true));

        if (handlers?.onError) {
            expressCheckoutElement.value?.on('loaderror', handlers?.onError);
            expressCheckoutElement.value?.on('cancel', handlers?.onError);
        }

        expressCheckoutElement.value?.on('confirm', async () => {
            if (!elements.value) {
                return Promise.reject({ code: StripeErrorCodes.ELEMENTS_NOT_MOUNTED });
            }

            const { error: submitError } = await elements.value.submit();

            if (submitError) {
                handlers?.onError && handlers?.onError();

                return;
            }

            handlers?.onConfirm && handlers?.onConfirm();
        });
    };

    const mountPayment = ({ id, ...handlers }: MountStripeElementsProps): void => {
        // Normal capture flow, only triggered if payment intent creation succeeds
        if (!elements.value) {
            throw { code: StripeErrorCodes.ELEMENTS_NOT_MOUNTED };
        }

        paymentElement.value = elements.value?.create('payment', {
            wallets: { applePay: 'auto', googlePay: 'auto' },
        });
        paymentElement.value?.mount(id);
        paymentElement.value?.once('loaderstart', () => (isPaymentElementLoaderStart.value = true));
        paymentElement.value?.once('ready', () => (isPaymentElementReady.value = true));
        handlers?.onFocus && paymentElement.value?.on('focus', handlers?.onFocus);
        handlers?.onError && paymentElement.value?.on('loaderror', handlers?.onError);
    };

    const destroy = (): void => {
        if (expressCheckoutElement.value) {
            expressCheckoutElement.value?.destroy();
            expressCheckoutElement.value = undefined;
            isPaymentElementReady.value = false;
            isExpressCheckoutReady.value = false;
        }

        if (paymentElement.value) {
            paymentElement.value?.destroy();
            paymentElement.value = undefined;
            isPaymentElementReady.value = false;
            isPaymentElementLoaderStart.value = false;
        }

        elements.value = null;
    };

    const confirmPayment = async (): Promise<PaymentIntentResult> => {
        if (!stripe.value) {
            return Promise.reject({ code: StripeErrorCodes.STRIPE_NOT_LOADED });
        }

        if (!elements.value) {
            return Promise.reject({ code: StripeErrorCodes.ELEMENTS_NOT_MOUNTED });
        }

        isConfirmingPayment.value = true;

        const result = await stripe.value.confirmPayment({
            elements: elements.value,
            confirmParams: {
                return_url: `${frontendBaseUrl}${route.fullPath}`,
                payment_method_data: {
                    billing_details: {
                        email: data.value?.user?.email,
                    },
                },
            },
            redirect: 'if_required',
        });
        isConfirmingPayment.value = false;

        return result;
    };

    const confirmSetup = async (): Promise<SetupIntentResult> => {
        if (!stripe.value) {
            return Promise.reject({ code: StripeErrorCodes.STRIPE_NOT_LOADED });
        }

        if (!elements.value) {
            return Promise.reject({ code: StripeErrorCodes.ELEMENTS_NOT_MOUNTED });
        }

        isConfirmingSetup.value = true;

        const result = await stripe.value.confirmSetup({
            elements: elements.value,
            confirmParams: {
                return_url: `${frontendBaseUrl}${route.fullPath}`,
                payment_method_data: {
                    billing_details: {
                        email: data.value?.user?.email,
                    },
                },
            },
            redirect: 'if_required',
        });

        isConfirmingSetup.value = false;

        return result;
    };

    return {
        isExpressCheckoutReady,
        isPaymentElementReady,
        isPaymentElementLoaderStart,
        isConfirmingPayment,
        isConfirmingSetup,
        elements,
        paymentElement,
        mountElements,
        mountPayment,
        mountExpressCheckout,
        destroy,
        confirmPayment,
        confirmSetup,
    };
};
